/**
 * management-panel.component.ts
 * Copyright (c) 2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, OnInit } from "@angular/core";
import { FormControl, FormArray, FormGroup, Validators } from "@angular/forms";

import { Group } from "../auth/identity/group";
import { User } from "../auth/identity/user";
import { UserService } from "../auth/identity/user.service";
import { AuthService } from "../auth/auth.service";
import { AlertService } from "../common/alert.service";
import { ModalService } from "../common/modal.service";
import { RULE_NAMES, Rule } from "../auth/permissions/rules";
import { ResetPasswordModalComponent } from "../auth/reset-password-modal.component";
import {
  CUSTOM,
  HEALTH_BENEFITS_ADVOCATE,
  REPORT_RUNNER,
  RoleTemplate,
  roleTemplates,
} from "./constants";
import { RemoveUserGroupModalComponent } from "./remove-user-group-modal.component";

@Component({
  template: `
    <div class="container">
      <div class="row">
        <div class="col-sm-4">
          <h1>Management Tools</h1>
        </div>
        <div class="col-sm-8">
          <div class="form-group">
            <label>Select group to manage:</label>
            <div class="row">
              <div class="col-sm-7 include-badge">
                <select class="form-control" [formControl]="groupControl">
                  <option
                    *ngFor="let gp of users.self.managedGroups"
                    [ngValue]="gp"
                  >
                    {{ gp.name }}
                  </option>
                </select>
                <span class="badge">{{ users.self.managedGroups.length }}</span>
              </div>
              <div class="col-sm-4">
                <button (click)="toggleNewUser()" class="btn btn-primary">
                  Add a new user
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div *ngIf="addNewUser" class="row">
        <div class="col-sm-2"></div>
        <div class="col-sm-8">
          <h4>New User Details</h4>
          <caption class="col-sm-8">
            Create a new user in
            <em>{{ group.name }}</em>
          </caption>
          <form
            [formGroup]="newUserForm"
            (ngSubmit)="onNewUserSubmit()"
            ngNativeValidate
          >
            <table class="table">
              <tbody>
                <tr>
                  <th>Login</th>
                  <td colspan="2">
                    <input
                      type="text"
                      formControlName="login"
                      class="form-control"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <th>Password</th>
                  <td colspan="2">
                    <input
                      type="password"
                      formControlName="password"
                      class="form-control"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <th>Name</th>
                  <td colspan="2">
                    <input
                      type="text"
                      formControlName="name"
                      class="form-control"
                      required
                    />
                  </td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td colspan="2">
                    <input
                      type="text"
                      formControlName="phone"
                      class="form-control"
                    />
                  </td>
                </tr>
                <tr>
                  <th>Locale</th>
                  <td>
                    <select
                      formControlName="countryCode"
                      class="form-control"
                      required
                    >
                      <option
                        *ngFor="
                          let country of users.countryCodes;
                          let i = index
                        "
                        [value]="i"
                      >
                        {{ country }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <select
                      formControlName="languageCode"
                      class="form-control"
                      required
                    >
                      <option
                        *ngFor="let lang of users.languageCodes; let i = index"
                        [value]="i"
                      >
                        {{ lang }}
                      </option>
                    </select>
                  </td>
                </tr>
              </tbody>
              <tbody>
                <tr>
                  <th>Rules</th>
                  <td colspan="2">
                    <select formControlName="roleTemplate" class="form-control">
                      <option
                        *ngFor="let roleTemplate of roleTemplates"
                        [ngValue]="roleTemplate"
                      >
                        {{ roleTemplate.name }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td formArrayName="groupRules">
                    <p>Group Rules</p>
                    <div
                      *ngFor="let option of this.group?.rules; let i = index"
                    >
                      <input
                        type="checkbox"
                        [formControlName]="i"
                        [attr.disabled]="
                          roleTemplate &&
                          roleTemplate.value &&
                          roleTemplate.value.name !== 'Custom'
                            ? ''
                            : null
                        "
                      />&nbsp;{{ this.getRuleName(option) }}
                    </div>
                  </td>
                  <td formArrayName="zoneRules">
                    <p>System Rules</p>
                    <div
                      *ngFor="
                        let option of this.users.self.rules;
                        let i = index
                      "
                    >
                      <input
                        type="checkbox"
                        [formControlName]="i"
                        [attr.disabled]="
                          roleTemplate &&
                          roleTemplate.value &&
                          roleTemplate.value.name !== 'Custom'
                            ? ''
                            : null
                        "
                      />&nbsp;{{ this.getRuleName(option) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <button type="submit" class="btn btn-primary">Submit</button>
          </form>
        </div>
        <div class="col-sm-2"></div>
      </div>
      <div class="row">
        <div class="col-sm-12">
          <table class="table striped">
            <caption>
              Users in
              <em>{{ groupControl.value?.name }}</em>
            </caption>
            <tr>
              <th class="debug-only text-center">ID</th>
              <th>Login</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
            <tbody *ngIf="group">
              <ng-container *ngFor="let user of group.users">
                <tr [ngClass]="{ 'selected-user-row': user.isSelf }">
                  <td class="text-center">{{ user.managementId }}</td>
                  <td>{{ user.login }}</td>
                  <td>{{ user.name }}</td>
                  <td>{{ user.org }}</td>
                  <td>n/a</td>
                  <td>
                    <button class="btn btn-info" (click)="open(user)">
                      Open
                    </button>
                  </td>
                </tr>
                <tr
                  *ngIf="
                    selectedUser &&
                    user.managementId === selectedUser.managementId
                  "
                >
                  <td colspan="5">
                    <div class="well details">
                      <h4>User Details</h4>
                      <table
                        *ngIf="selectedUser.loadedManagementDetails"
                        class="table"
                      >
                        <tbody>
                          <tr>
                            <th>Name</th>
                            <td>
                              {{ selectedUser.name }}
                              <em
                                *ngIf="selectedUser.isSelf"
                                class="text-success text-right"
                              >
                                (This is you.)
                              </em>
                            </td>
                            <td colspan="2">
                              <input
                                type="text"
                                [formControl]="newName"
                                class="form-control"
                                disabled
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>Phone</th>
                            <td>
                              <!--<span class="glyphicon glyphicon-phone-alt"></span>-->
                              <a [href]="'tel:' + selectedUser.phone">
                                {{ selectedUser.phone }}
                              </a>
                            </td>
                            <td colspan="2">
                              <input
                                type="text"
                                [formControl]="newPhone"
                                class="form-control"
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>Locale</th>
                            <td>
                              <kbd> {{ selectedUser.countryCode }} </kbd
                              >&mdash;<kbd>
                                {{ selectedUser.languageCode }}
                              </kbd>
                            </td>
                            <td>
                              <select
                                [formControl]="newCountry"
                                class="form-control"
                              >
                                <option
                                  *ngFor="
                                    let country of users.countryCodes;
                                    let i = index
                                  "
                                  [value]="i"
                                >
                                  {{ country }}
                                </option>
                              </select>
                            </td>
                            <td>
                              <select
                                [formControl]="newLanguage"
                                class="form-control"
                              >
                                <option
                                  *ngFor="
                                    let lang of users.languageCodes;
                                    let i = index
                                  "
                                  [value]="i"
                                >
                                  {{ lang }}
                                </option>
                              </select>
                            </td>
                          </tr>
                          <tr>
                            <td colspan="2"></td>
                            <td colspan="2">
                              <button
                                class="btn btn-success p100"
                                (click)="saveUserDetails()"
                                [disabled]="userDetails.pristine"
                              >
                                Save Changes Above
                              </button>
                            </td>
                          </tr>
                        </tbody>
                        <tbody>
                          <tr>
                            <th>Credentials</th>
                            <td>
                              <!--<span class="glyphicon glyphicon-envelope"></span>-->
                              <a [href]="'mailto:' + selectedUser.login">
                                {{ selectedUser.login }}
                              </a>
                            </td>
                            <td colspan="2">
                              <button
                                class="btn btn-primary p100"
                                (click)="requestPasswordReset()"
                              >
                                Reset Password...
                              </button>
                            </td>
                          </tr>
                        </tbody>
                        <tbody>
                          <tr>
                            <th>Status</th>
                            <td>
                              <span
                                [class.text-success]="selectedUser.isActive"
                                [class.text-danger]="!selectedUser.isActive"
                              >
                                {{
                                  selectedUser.isActive ? "Active" : "INACTIVE"
                                }}
                              </span>
                            </td>
                            <td colspan="2">
                              <button
                                class="btn btn-primary p100"
                                (click)="toggleUserActiveness()"
                              >
                                Toggle Status
                              </button>
                            </td>
                          </tr>
                        </tbody>
                        <tbody>
                          <tr>
                            <td colspan="1">
                              <button
                                class="btn btn-danger p100"
                                (click)="removeUserFromGroup()"
                              >
                                Remove from this Group
                              </button>
                            </td>
                            <td colspan="1">
                              <button
                                class="btn btn-success p100"
                                (click)="addUsertoOtherGroup()"
                                [disabled]="getGroupsToAdd().length === 0"
                              >
                                Add to Other Group
                              </button>
                            </td>
                            <td *ngIf="addUserToGroup">
                              <form
                                [formGroup]="addToGroupForm"
                                (ngSubmit)="onAddUserToOtherGroupSubmit()"
                                ngNativeValidate
                              >
                                <select
                                  class="form-control"
                                  formControlName="groups"
                                  required
                                >
                                  <option
                                    *ngFor="let gp of getGroupsToAdd()"
                                    [ngValue]="gp"
                                  >
                                    {{ gp.name }}
                                  </option>
                                </select>
                                <button type="submit" class="btn btn-primary">
                                  Add
                                </button>
                              </form>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <p
                        *ngIf="!selectedUser.loadedManagementDetails"
                        class="text-danger"
                      >
                        No details received.
                      </p>
                    </div>
                  </td>
                </tr>
              </ng-container>
            </tbody>
            <tr *ngIf="!group">
              <td colspan="3" class="text-center">
                <h3>No group selected</h3>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .details tbody + tbody > tr {
        border-top: rgb(30, 30, 30);
      }
      .details td,
      .details th {
        vertical-align: middle;
      }
      button[type="submit"] {
        float: right;
      }
      .include-badge {
        position: relative;
      }
      .badge {
        position: absolute;
        top: -20%;
        left: 0;
      }
      .selected-user-row {
        background-color: lightblue;
      }
    `,
  ],
})
export class ManagementPanelComponent implements OnInit {
  constructor(
    public users: UserService,
    public auth: AuthService,
    private modals: ModalService,
    public alerts: AlertService
  ) {}

  ngOnInit(): void {
    this.groupControl.valueChanges.subscribe((g: Group) => {
      this.refreshGroup(g);
    });
    this.roleTemplate.valueChanges.subscribe((roleTemplate: RoleTemplate) => {
      if (roleTemplate && roleTemplate.name !== "Custom") {
        const groupRules = this.newUserForm.get("groupRules") as FormArray;
        this.group.rules.forEach((rule: Rule, index: number) => {
          if (roleTemplate.groupRules.includes(rule)) {
            groupRules.at(index).setValue(true);
          } else {
            groupRules.at(index).setValue(false);
          }
        });
        const zoneRules = this.newUserForm.get("zoneRules") as FormArray;
        this.users.self.rules.forEach((rule: Rule, index: number) => {
          if (roleTemplate.zoneRules.includes(rule)) {
            zoneRules.at(index).setValue(true);
          } else {
            zoneRules.at(index).setValue(false);
          }
        });
      }
    });
    if (this.users.self.managedGroups.length > 0) {
      this.groupControl.setValue(this.users.self.managedGroups[0]);
    }
  }

  public groupControl: FormControl = new FormControl();

  public selectedUser: User;

  public group: Group;

  public addNewUser: boolean = false;

  public addUserToGroup: boolean = false;

  public roleTemplates: RoleTemplate[] = roleTemplates;

  public newName: FormControl = new FormControl();
  public newPhone: FormControl = new FormControl();
  public newCountry: FormControl = new FormControl();
  public newLanguage: FormControl = new FormControl();
  public userDetails: FormArray = new FormArray([
    this.newName,
    this.newPhone,
    this.newCountry,
    this.newLanguage,
  ]);

  public newUserLogin: FormControl = new FormControl("", Validators.required);
  public newUserPassword: FormControl = new FormControl(
    "",
    Validators.required
  );
  public newUserName: FormControl = new FormControl("", Validators.required);
  public newUserPhone: FormControl = new FormControl("", Validators.required);
  public newUserCountryCode: FormControl = new FormControl(
    "",
    Validators.required
  );
  public newUserLanguageCode: FormControl = new FormControl(
    "",
    Validators.required
  );
  public newUserZoneRules: FormArray = new FormArray([]);
  public newUserGroupRules: FormArray = new FormArray([]);
  public roleTemplate: FormControl = new FormControl(CUSTOM);
  public newUserForm: FormGroup = new FormGroup({
    login: this.newUserLogin,
    password: this.newUserPassword,
    name: this.newUserName,
    phone: this.newUserPhone,
    countryCode: this.newUserCountryCode,
    languageCode: this.newUserLanguageCode,
    roleTemplate: this.roleTemplate,
    zoneRules: this.newUserZoneRules,
    groupRules: this.newUserGroupRules,
  });

  public addToGroup: FormControl = new FormControl();
  public addToGroupRules: FormArray = new FormArray([]);

  public addToGroupForm: FormGroup = new FormGroup({
    groups: this.addToGroup,
    groupRules: this.newUserGroupRules,
  });

  public refreshGroup(group: Group): void {
    this.selectedUser = null;
    this.users.getGroupUsers(group).subscribe((group: Group) => {
      this.group = group;
      this.addUserToGroup = false;
      this.addToGroupForm.reset();
      this.newUserForm.get("roleTemplate").setValue(CUSTOM);
      this.newUserForm.setControl("zoneRules", new FormArray([]));
      this.users.self.rules.forEach(() => {
        (this.newUserForm.get("zoneRules") as FormArray).push(
          new FormControl(false)
        );
      });
      this.newUserForm.setControl("groupRules", new FormArray([]));
      group.rules.forEach(() => {
        (this.newUserForm.get("groupRules") as FormArray).push(
          new FormControl(false)
        );
        (this.addToGroupForm.get("groupRules") as FormArray).push(
          new FormControl(false)
        );
      });
    });
  }

  public open(user: User): void {
    this.selectedUser = user;
    this.users.requestUserDetails(user).subscribe(() => {
      this.addUserToGroup = false;
      this.addToGroupForm.reset();
      this.newName.reset(user.name);
      this.newPhone.reset(user.phone);
      this.newCountry.reset(user.countryCodeID);
      this.newLanguage.reset(user.languageCodeID);
    });
  }
  public toggleUserActiveness(): void {
    const changeUser = this.selectedUser;
    this.users
      .requestUserActiveStatusChange(
        this.group,
        changeUser,
        !changeUser.isActive
      )
      .subscribe(
        (response) => {
          this.open(changeUser);
          this.alerts.success("User status saved successfully");
        },
        (error) => {}
      );
  }
  public saveUserDetails(): void {
    const changeUser = this.selectedUser;
    const o = changeUser.isSelf
      ? this.users.requestUserEditRequest(
          this.newName.value,
          this.newPhone.value,
          this.newCountry.value,
          this.newLanguage.value
        )
      : this.users.requestOtherUserEditRequest(
          changeUser.objectId,
          this.newName.value,
          this.newPhone.value,
          this.newCountry.value,
          this.newLanguage.value
        );

    o.subscribe(
      (response) => {
        this.open(changeUser);
        this.alerts.success("User details saved successfully");
      },
      (error) => {}
    );

    this.userDetails.markAsPristine();
  }
  public requestPasswordReset(): void {
    this.modals
      .observe(ResetPasswordModalComponent, {
        objectId: this.selectedUser.objectId,
      })
      .subscribe((saved: boolean) =>
        saved
          ? this.alerts.success("User password reset successfully")
          : this.alerts.error("User password reset unsuccessful")
      );
  }

  public getRuleName(rule: Rule): string {
    return RULE_NAMES.get(rule) || `${Rule[rule]} (${rule})`;
  }

  public toggleNewUser(): void {
    this.addNewUser = !this.addNewUser;
  }

  public onNewUserSubmit(): void {
    const o = this.users.requestUserCreate(
      this.newUserForm.get("login").value,
      this.newUserForm.get("password").value,
      this.newUserForm.get("name").value,
      this.newUserForm.get("phone").value,
      Number(this.newUserForm.get("countryCode").value),
      Number(this.newUserForm.get("languageCode").value),
      this.group,
      this.group.rules.filter((_rule: Rule, index: number) => {
        return this.newUserForm.get("groupRules").value[index] === true;
      }),
      this.users.self.rules.filter((_rule: Rule, index: number) => {
        return this.newUserForm.get("zoneRules").value[index] === true;
      })
    );
    o.subscribe(
      (response) => {
        this.newUserForm.reset();
        this.addNewUser = false;
        this.refreshGroup(this.group);
        this.alerts.success("User created successfully");
      },
      (error) => this.alerts.error("Failed to create user")
    );
  }

  public getGroupsToAdd(): Group[] {
    const managingGroups = this.users.self.managedGroups;
    const selectedUserGroup = this.selectedUser.groups;
    return managingGroups.filter(
      (managingGroup: Group) =>
        !selectedUserGroup.find(
          (userGroup: Group) => userGroup.id === managingGroup.id
        )
    );
  }

  public onAddUserToOtherGroupSubmit(): void {
    const selectedUserGroupRules = this.selectedUser.groups.find(
      (group: Group) => group.id === this.group.id
    ).rules;
    const o = this.users.requestAddUserToGroupRequest(
      this.selectedUser.objectId,
      this.addToGroupForm.get("groups").value,
      selectedUserGroupRules
    );
    o.subscribe(
      (response) => {
        this.addToGroupForm.reset();
        this.addUserToGroup = false;
        this.alerts.success("User added to group successfully");
      },
      (error) => this.alerts.error("Failed to add user to group")
    );
  }

  public removeUserFromGroup(): void {
    // const o = this.users.requestRemoveUserGroupsRequest(this.selectedUser.objectId, [this.group.id])
    // o.subscribe((response) => {
    //   this.alerts.success("User removed from group successfully");
    // }, (error) => this.alerts.error("Failed to remove user from group"))
    this.modals
      .observe(RemoveUserGroupModalComponent, {
        objectId: this.selectedUser.objectId,
        groupId: this.group.id,
      })
      .subscribe((saved: boolean) => {
        this.refreshGroup(this.group);
        saved
          ? this.alerts.success("User removed from group successfully")
          : this.alerts.error("Failed to remove user from group");
      });
  }

  public addUsertoOtherGroup(): void {
    this.addUserToGroup = !this.addUserToGroup;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/management/management-panel.component.ts